import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { type GiftCardPurchaseFormData } from '@/features/cms/components/CMS/types'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { Column, Box } from '@shipt/design-system-layouts'
import { GiftCardChoices } from '@/features/cms/components/CMS/components/GiftCardPurchaseForm/GiftCardChoices'
import { screenSizes } from '@shipt/design-system-themes'
import { Body, Headline } from '@shipt/design-system-typography'
import styled from 'styled-components'
import { GiftDesignChoices } from '@/features/cms/components/CMS/components/GiftCardPurchaseForm/GiftDesignChoices'
import {
  type GiftCardFormInput,
  GiftCardRecipientForm,
} from '@/features/cms/components/CMS/components/GiftCardPurchaseForm/GiftCardRecipientForm'
import { useForm } from 'react-hook-form'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { DynamicGiftCardOrderOverviewModal } from '@/features/purchase/components/Modal/GiftCardModals/GiftCardOrderOverviewModal/DynamicGiftCardOrderOverviewModal'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { useEffect } from 'react'
import { getItem, setItem } from '@/features/shared/utils/localStorage'
import { parseJson } from '@/features/shared/utils/parseJson'
import { DynamicGiftCardPurchaseConfirmationDialog } from '@/features/purchase/components/Modal/GiftCardModals/GiftCardPurchaseConfirmationDialog/DynamicGiftCardPurchaseConfirmationDialog'
import { upsellErrorMessage } from '@/features/purchase/components/Modal/GiftCardModals/GiftCardOrderOverviewModal'
import { routes } from '@shared/constants/routes'
import {
  GIFT_CARD_FORM_DATA,
  getDefaultFormValues,
  getGiftCardChoiceAnalyticsMessageGoal,
} from '@/features/cms/components/CMS/components/GiftCardPurchaseForm/utils'
import { useSearchParams } from 'next/navigation'
import { theme } from '@/features/shared/constants/shiptTheme'

/**
 * This component corresponds to the `gift_card_form` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/gift_card_form
 * - **Production** - https://cms.shipt.com/content-type/pool/gift_card_form
 */
export const GiftCardPurchaseForm = ({ content_type_id, id, data }: GiftCardPurchaseFormData) => {
  const { openDialog } = useHandleDialog()
  const { trackCMSElementClicked } = useCMSTrackingEvents()

  const router = useSegwayRouter()
  const searchParams = useSearchParams()
  const upsell = searchParams?.get('upsell')
  const purchase_success = searchParams?.get('purchase_success')

  const getLocalStorageValue = () => {
    const localFormData = getItem(GIFT_CARD_FORM_DATA)
    return parseJson<GiftCardFormInput>(localFormData)
  }

  const giftCardForm = useForm<GiftCardFormInput>({
    defaultValues: getDefaultFormValues(data),
    values: getLocalStorageValue(),
    shouldFocusError: false,
    mode: 'all',
  })
  const { handleSubmit, reset } = giftCardForm

  const onGiftCardFormSubmit = (formData: GiftCardFormInput) => {
    trackCMSElementClicked({
      content: 'add_payment_method',
      message_goal: getGiftCardChoiceAnalyticsMessageGoal(formData.cardChoice),
      next_location: 'add_payment_modal',
      type: 'button',
    })
    setItem(GIFT_CARD_FORM_DATA, JSON.stringify(formData))
    return openDialog(DynamicGiftCardOrderOverviewModal, {
      formData,
    })
  }

  useEffect(() => {
    if (purchase_success === 'true') {
      reset(getDefaultFormValues(data))
      if (upsell === 'error') {
        openDialog(DynamicGiftCardPurchaseConfirmationDialog, {
          error: { message: upsellErrorMessage },
        })
      } else {
        openDialog(DynamicGiftCardPurchaseConfirmationDialog)
      }
      router.replace(routes.GIFT.url)
    }
  }, [purchase_success, openDialog, upsell, reset, router, data])

  useEffect(() => {
    localStorage.removeItem(GIFT_CARD_FORM_DATA)
  }, [])

  try {
    return (
      <GridLayoutContainer {...getCMSWrapperProps({ content_type_id, id })}>
        <ColumnWrapper
          className="fullWidth contentAtTablet"
          as="form"
          onSubmit={handleSubmit(onGiftCardFormSubmit)}
          align="center"
          spacing="xxl"
          id="gift-membership"
        >
          <Headline as="h2" align="center" size="xl">
            Choose your gift card
          </Headline>
          <Box padding={[0, 'lg']}>
            <Body size="xl" align="center">
              Select your gift card design, then choose the value or membership type below.
            </Body>
          </Box>

          <GiftDesignChoices designImages={data.gift_cards} giftCardForm={giftCardForm} />
          <ChoicesWrapper
            align={{ base: 'start', md: 'center' }}
            padding={{ base: ['lg'], sm: ['xl'], md: ['md', '4xl'] }}
            spacing={{ base: 'lg', md: 'xxl' }}
          >
            <GiftCardChoices giftCardForm={giftCardForm} />
          </ChoicesWrapper>
          <GiftCardRecipientForm giftCardForm={giftCardForm} />
        </ColumnWrapper>
      </GridLayoutContainer>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const ColumnWrapper = styled(Column)`
  /* Browser uses set margin-top when element is being scrolled into view */
  scroll-margin-top: 7rem;
`

const ChoicesWrapper = styled(Column)`
  background-color: ${theme.color.background.base.neutral.subtle_100};
  max-width: 50rem;
  width: 100%;
  min-height: 600px;

  @media ${screenSizes.tablet} {
    border-radius: 1rem;
    min-height: 380px;
  }
`
